import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { Eye, EyeOff, Mail, Lock, User, AlertCircle, Ticket, CalendarPlus, Headphones, Megaphone, Check, Info } from 'lucide-react';
import { authAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';

const APPLE_SERVICE_ID = process.env.REACT_APP_APPLE_SERVICE_ID;

const ACCOUNT_TYPES = [
  { value: 'attending', title: 'Attendee', subtitle: 'Find events, buy tickets', Icon: Ticket },
  { value: 'organizing', title: 'Organiser', subtitle: 'Host events, sell tickets', Icon: CalendarPlus },
  { value: 'dj', title: 'DJ', subtitle: 'Play and run event nights', Icon: Headphones },
  { value: 'promoting', title: 'Promoter', subtitle: 'Promote events, earn rewards', Icon: Megaphone },
];

const ACCOUNT_TYPE_LABELS = { attending: 'Attendee', organizing: 'Organiser', dj: 'DJ', promoting: 'Promoter' };
const SEARCH_PARAM_TYPES = { organiser: 'organizing', dj: 'dj', promoter: 'promoting' };

const homePath = (account) => {
  if (['super_admin', 'finance_admin', 'translator_admin', 'support_admin'].includes(account.role)) return '/admin';
  if (account.role === 'organiser' && account.account_type === 'organizing') return '/organiser';
  return '/explore';
};

// No response at all means the request never reached the API (offline, or
// blocked by a browser extension), which a generic failure message would hide.
const authErrorMessage = (err) => err.response?.data?.detail
  || (err.response ? 'Authentication failed' : "Couldn't reach SAC Events. Check your connection or disable ad/privacy blockers for this site.");

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const { login, user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [tab, setTab] = useState(searchParams.get('tab') === 'register' ? 'register' : 'login');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [accountType, setAccountType] = useState(SEARCH_PARAM_TYPES[searchParams.get('type')] || 'attending');
  // Only a sign-up decides the account type; signing in keeps whatever the account already is.
  const signupAccountType = tab === 'register' ? accountType : undefined;

  // Runs both for an already-signed-in visit and right after login() sets the
  // user, so the role-based landing page is decided here only.
  // ?redirect=/invite/... (only same-site paths) returns the user where they came from.
  const redirect = searchParams.get('redirect');
  const safeRedirect = redirect && redirect.startsWith('/') && !redirect.startsWith('//') ? redirect : null;
  useEffect(() => {
    if (user) navigate(safeRedirect || homePath(user));
  }, [user, navigate, safeRedirect]);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const completeLogin = (resp, welcomeMsg) => {
    const account = resp.data.user || resp.data;
    login(account, resp.data.token);
    toast.success(welcomeMsg || `Welcome back, ${account.name}!`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (tab === 'login') {
        const resp = await authAPI.login({ email: form.email, password: form.password });
        completeLogin(resp);
      } else {
        const resp = await authAPI.register({ email: form.email, password: form.password, name: form.name, accountType });
        completeLogin(resp, 'Account created!');
      }
    } catch (err) {
      const msg = authErrorMessage(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const completeSocialLogin = (resp) => completeLogin(resp, `Welcome, ${(resp.data.user || resp.data).name}!`);

  const handleGoogleSuccess = async (credentialResponse) => {
    setError('');
    setLoading(true);
    try {
      const resp = await authAPI.googleLogin(credentialResponse.credential, signupAccountType);
      completeSocialLogin(resp);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Google sign-in failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!APPLE_SERVICE_ID) return;
    if (document.getElementById('appleid-signin-script')) return;
    const script = document.createElement('script');
    script.id = 'appleid-signin-script';
    script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
    script.async = true;
    document.body.appendChild(script);
  }, []);

  const handleAppleLogin = async () => {
    if (!window.AppleID || !APPLE_SERVICE_ID) {
      toast.error('Apple sign-in is not available right now. Please use another method.');
      return;
    }
    setError('');
    try {
      window.AppleID.auth.init({
        clientId: APPLE_SERVICE_ID,
        scope: 'name email',
        redirectURI: window.location.origin + '/auth',
        usePopup: true,
      });
      const { authorization, user: appleUser } = await window.AppleID.auth.signIn();
      setLoading(true);
      const fullName = appleUser?.name ? `${appleUser.name.firstName || ''} ${appleUser.name.lastName || ''}`.trim() : undefined;
      const resp = await authAPI.appleLogin(authorization.id_token, fullName, signupAccountType);
      completeSocialLogin(resp);
    } catch (err) {
      if (err?.error === 'popup_closed_by_user') return;
      const msg = err.response?.data?.detail || 'Apple sign-in failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] pt-16 flex items-center justify-center px-4 page-enter" data-testid="auth-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/">
            <span className="font-black text-3xl tracking-tighter uppercase" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              <span style={{ color: '#E63946' }}>SAC</span>
              <span className="text-white"> EVENTS</span>
            </span>
          </Link>
          <p className="text-gray-500 text-sm mt-2">
            {tab === 'login' ? t('loginTitle') : t('registerTitle')}
          </p>
        </div>

        <div className="bg-[#0A0A0A] border border-gray-800 rounded-xl p-8">
          {/* Tab switcher */}
          <div className="flex rounded-lg border border-gray-800 overflow-hidden mb-6">
            {['login', 'register'].map(tabName => (
              <button
                key={tabName}
                onClick={() => { setTab(tabName); setError(''); }}
                className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                  tab === tabName ? 'bg-red-600 text-white' : 'text-gray-500 hover:text-white'
                }`}
                data-testid={`tab-${tabName}`}
              >
                {tabName === 'login' ? t('signIn') : t('signUp')}
              </button>
            ))}
          </div>

          {/* Account type — asked up front so every sign-up path (email, Google, Apple) uses it */}
          {tab === 'register' && (
            <div className="mb-6" data-testid="account-type-picker">
              <p className="text-gray-400 text-xs font-medium uppercase tracking-wider mb-2">I'm signing up as</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" role="radiogroup">
                {ACCOUNT_TYPES.map(({ value, title, subtitle, Icon }) => {
                  const selected = accountType === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => setAccountType(value)}
                      className={`relative text-left rounded-lg border p-3 transition-colors ${
                        selected ? 'border-red-600 bg-red-600/10' : 'border-gray-700 hover:border-gray-500'
                      }`}
                      data-testid={`account-type-${value}`}
                    >
                      {selected && (
                        <span className="absolute top-2 right-2 w-4 h-4 rounded-full bg-red-600 flex items-center justify-center">
                          <Check size={10} className="text-white" strokeWidth={3} />
                        </span>
                      )}
                      <Icon size={18} className={selected ? 'text-red-500' : 'text-gray-500'} />
                      <p className="text-white text-sm font-bold mt-2">{title}</p>
                      <p className="text-gray-500 text-xs mt-0.5 leading-snug">{subtitle}</p>
                    </button>
                  );
                })}
              </div>
              {(accountType === 'dj' || accountType === 'organizing') && (
                <p className="flex gap-2 text-gray-400 text-xs mt-3 leading-snug" data-testid="dj-approval-note">
                  <Info size={13} className="shrink-0 mt-px text-red-500" />
                  Our team reviews {accountType === 'dj' ? 'DJ' : 'organiser'} requests. You can browse and book events while you wait.
                </p>
              )}
            </div>
          )}

          {/* Social login */}
          <div className="mb-3 flex justify-center [&>div]:w-full">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={() => { setError('Google sign-in failed'); toast.error('Google sign-in failed'); }}
              theme="filled_black"
              width="100%"
              text={tab === 'login' ? 'signin_with' : 'signup_with'}
            />
          </div>

          <button
            type="button"
            onClick={handleAppleLogin}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 h-10 bg-[#202124] border border-gray-700 text-white rounded text-sm font-medium hover:border-gray-500 mb-4 transition-colors disabled:opacity-60"
            data-testid="apple-login-btn"
          >
            <svg width="15" height="15" viewBox="0 0 384 512" fill="currentColor" aria-hidden="true">
              <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
            </svg>
            {tab === 'login' ? 'Sign in with Apple' : 'Sign up with Apple'}
          </button>

          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-px bg-gray-800" />
            <span className="text-gray-600 text-xs uppercase">{t('orContinueWith')}</span>
            <div className="flex-1 h-px bg-gray-800" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {tab === 'register' && (
              <div>
                <label className="block text-gray-400 text-xs font-medium uppercase tracking-wider mb-1.5">{t('fullName')}</label>
                <div className="relative">
                  <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                  <input
                    name="name"
                    type="text"
                    value={form.name}
                    onChange={handleChange}
                    required
                    placeholder="Your full name"
                    className="w-full bg-[#111] border border-gray-700 text-white pl-9 pr-3 py-2.5 rounded text-sm"
                    data-testid="name-input"
                  />
                </div>
              </div>
            )}
            <div>
              <label className="block text-gray-400 text-xs font-medium uppercase tracking-wider mb-1.5">{t('email')}</label>
              <div className="relative">
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  placeholder="your@email.com"
                  className="w-full bg-[#111] border border-gray-700 text-white pl-9 pr-3 py-2.5 rounded text-sm"
                  data-testid="email-input"
                />
              </div>
            </div>
            <div>
              <label className="block text-gray-400 text-xs font-medium uppercase tracking-wider mb-1.5">{t('password')}</label>
              <div className="relative">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  name="password"
                  type={showPass ? 'text' : 'password'}
                  value={form.password}
                  onChange={handleChange}
                  required
                  placeholder="••••••••"
                  className="w-full bg-[#111] border border-gray-700 text-white pl-9 pr-10 py-2.5 rounded text-sm"
                  data-testid="password-input"
                />
                <button type="button" onClick={() => setShowPass(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white">
                  {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 text-red-400 text-sm rounded-lg p-3">
                <AlertCircle size={14} />
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary py-3 rounded text-sm font-bold flex items-center justify-center gap-2 mt-2"
              data-testid="auth-submit-btn"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                tab === 'login' ? t('signIn') : `${t('signUp')} as ${ACCOUNT_TYPE_LABELS[accountType]}`
              )}
            </button>
          </form>

          <p className="text-center text-gray-600 text-xs mt-4">
            {tab === 'login' ? t('noAccount') : t('haveAccount')}{' '}
            <button onClick={() => setTab(tab === 'login' ? 'register' : 'login')} className="text-red-400 hover:text-red-300 font-semibold">
              {tab === 'login' ? t('signUp') : t('signIn')}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
